import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { uuidv7, type Conversation, type DirectMessage, type SearchResults, type UserSummary } from "@atrium/shared";
import { ApiError, api, errorMessage, qs } from "../../lib/api";
import { cn } from "../../lib/cn";
import { timeAgo } from "../../lib/format";
import { useDebounced, useDocumentTitle, useIsDesktop } from "../../lib/hooks";
import { getSocket } from "../../lib/socket";
import { Avatar } from "../../components/ui/Avatar";
import { Button, IconButton } from "../../components/ui/Button";
import { Badge, EmptyState, ErrorState } from "../../components/ui/Controls";
import { Dialog, confirm } from "../../components/ui/Dialog";
import { Input } from "../../components/ui/Input";
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from "../../components/ui/Menu";
import { RowSkeleton } from "../../components/ui/Skeleton";
import { Spinner } from "../../components/ui/Spinner";
import { toast } from "../../components/ui/Toast";
import { IconBack, IconCheckRead, IconCompose, IconCopy, IconEdit, IconLock, IconLogout, IconMessages, IconMore, IconSearch, IconUser, IconUserPlus, IconX } from "../../components/icons";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "../../components/ui/ContextMenu";
import { copyWithToast } from "../chat/MessageMenus";
import { useMe } from "../auth/AuthProvider";
import { MessageList } from "../chat/MessageList";
import { ChatComposer, type ComposerSend } from "../chat/ChatComposer";
import { gifEmbed } from "../chat/ChatGif";
import { chatQueryOptions, patchMessage, removeMessage, upsertMessage, useChatFocus, useTyping, type MessagePage } from "../chat/cache";
import { deleteMessageWithUndo } from "../chat/deleteWithUndo";
import type { ChatMessage, MessageActions } from "../chat/types";
import { CallButton, CallPanel } from "../voice/CallPanel";
import { KeyChangedError } from "../../lib/e2ee/session";
import { encryptedMedia, takeDescriptors, uploadEncryptedImage } from "../../lib/e2ee/files";
import { decryptConversation, decryptConversations, decryptDms, encryptForConversation, encryptedPreview, ensureEncrypted, rememberPlaintext, willEncrypt, withFreshKey } from "./e2eeMessages";
import { RecoveryCodeNudge, SetUpDialog, UnlockDialog, VerifyDialog, useE2ee, useMemberTrust } from "./E2eeUi";

export const useConversations = () =>
  useQuery({ queryKey: ["conversations"], queryFn: () => api.get<{ items: Conversation[] }>("/api/conversations").then((r) => decryptConversations(r.items)) });

export function conversationTitle(c: Conversation, meId: string) {
  if (c.name) return c.name;
  const others = c.members.filter((m) => m.id !== meId);
  if (!others.length) return "Just you";
  return others.map((m) => m.displayName).join(", ");
}

export function ConversationAvatar({ c, meId, size = "lg" }: { c: Conversation; meId: string; size?: "md" | "lg" }) {
  const others = c.members.filter((m) => m.id !== meId);
  if (c.kind === "direct" || others.length <= 1) {
    const u = others[0] ?? c.members[0];
    return <Avatar src={u?.avatarUrl} name={u?.displayName ?? "?"} size={size} />;
  }
  const [a, b] = others;
  return (
    <span className={cn("relative shrink-0", size === "lg" ? "size-10" : "size-9")}>
      <Avatar src={a.avatarUrl} name={a.displayName} size="sm" className="absolute left-0 top-0" />
      <Avatar src={b.avatarUrl} name={b.displayName} size="sm" className="absolute bottom-0 right-0 rounded-full ring-2 ring-bg-subtle" />
    </span>
  );
}

function PeoplePicker({ selected, onChange, exclude = [] }: { selected: UserSummary[]; onChange: (u: UserSummary[]) => void; exclude?: string[] }) {
  const me = useMe();
  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim(), 200);
  const search = useQuery({
    queryKey: ["search-users", dq],
    queryFn: () => api.get<SearchResults>(`/api/search?type=users&q=${encodeURIComponent(dq)}`),
    enabled: dq.length > 0,
  });
  const results = (search.data?.users ?? []).filter((u) => u.id !== me.id && !exclude.includes(u.id));
  return (
    <div className="space-y-3">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((u) => (
            <span key={u.id} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-surface-2 pl-1 pr-1.5 text-sm">
              <Avatar src={u.avatarUrl} name={u.displayName} size="xs" />
              {u.displayName}
              <button aria-label={`Remove ${u.displayName}`} onClick={() => onChange(selected.filter((s) => s.id !== u.id))} className="rounded-full p-0.5 text-fg-3 hover:text-fg">
                <IconX className="icon size-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <IconSearch className="icon pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-fg-3" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" className="pl-9" aria-label="Search people" />
      </div>
      <div className="min-h-32">
        {search.isFetching && !search.data && (
          <div className="flex justify-center py-6 text-fg-3">
            <Spinner className="size-4" />
          </div>
        )}
        {results.map((u) => {
          const on = selected.some((s) => s.id === u.id);
          return (
            <button
              key={u.id}
              onClick={() => onChange(on ? selected.filter((s) => s.id !== u.id) : [...selected, u])}
              aria-pressed={on}
              className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-surface"
            >
              <Avatar src={u.avatarUrl} name={u.displayName} size="md" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{u.displayName}</div>
                <div className="truncate text-xs text-fg-3">@{u.username}</div>
              </div>
              <span className={cn("flex size-5 items-center justify-center rounded-full border", on ? "border-accent bg-accent text-accent-fg" : "border-line-strong")}>
                {on && <IconCheckRead className="icon size-3.5" />}
              </span>
            </button>
          );
        })}
        {dq && search.data && results.length === 0 && <p className="px-2 py-4 text-sm text-fg-3">No one found.</p>}
        {!dq && <p className="px-2 py-4 text-sm text-fg-3">Search by name or username.</p>}
      </div>
    </div>
  );
}

function NewConversationDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<UserSummary[]>([]);
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (!open) {
      setSelected([]);
      setName("");
    }
  }, [open]);
  const start = async () => {
    setPending(true);
    try {
      const c = await api.post<Conversation>("/api/conversations", { userIds: selected.map((u) => u.id), name: selected.length > 1 && name.trim() ? name.trim() : undefined });
      await qc.invalidateQueries({ queryKey: ["conversations"] });
      onOpenChange(false);
      navigate(`/messages/${c.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New message"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!selected.length} loading={pending} onClick={() => void start()}>
            {selected.length > 1 ? "Create group" : "Start"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {selected.length > 1 && <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name (optional)" maxLength={50} aria-label="Group name" />}
        <PeoplePicker selected={selected} onChange={setSelected} />
      </div>
    </Dialog>
  );
}

/** `compact` is the desktop list beside the detail pane, which carries the call to action. */
/** Right-click / long-press on a conversation in the list. */
function ConversationContextMenu({ c, meId, children }: { c: Conversation; meId: string; children: ReactElement }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const other = c.kind === "direct" ? c.members.find((m) => m.id !== meId) : undefined;
  const markRead = () => {
    if (!c.lastMessage) return;
    qc.setQueryData<Conversation[]>(["conversations"], (old) => old?.map((x) => (x.id === c.id ? { ...x, unreadCount: 0 } : x)));
    void api.post(`/api/conversations/${c.id}/read`, { messageId: c.lastMessage.id }).catch((e) => toast.error(errorMessage(e)));
  };
  const leave = async () => {
    const ok = await confirm({ title: "Leave this group?", description: "You won't receive new messages unless someone adds you back.", confirmLabel: "Leave", danger: true });
    if (!ok) return;
    try {
      await api.post(`/api/conversations/${c.id}/leave`);
      qc.setQueryData<Conversation[]>(["conversations"], (old) => old?.filter((x) => x.id !== c.id));
      navigate("/messages");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem icon={IconCheckRead} disabled={!c.unreadCount || !c.lastMessage} onSelect={markRead}>
          Mark as read
        </ContextMenuItem>
        {other && (
          <ContextMenuItem icon={IconUser} onSelect={() => navigate(`/u/${other.username}`)}>
            Profile
          </ContextMenuItem>
        )}
        <ContextMenuSeparator />
        {other ? (
          <>
            <ContextMenuItem icon={IconCopy} onSelect={() => void copyWithToast(other.username)}>
              Copy username
            </ContextMenuItem>
            <ContextMenuItem icon={IconCopy} onSelect={() => void copyWithToast(other.id)}>
              Copy user ID
            </ContextMenuItem>
          </>
        ) : (
          <ContextMenuItem icon={IconCopy} onSelect={() => void copyWithToast(c.id)}>
            Copy group ID
          </ContextMenuItem>
        )}
        {c.kind === "group" && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem icon={IconLogout} danger onSelect={() => void leave()}>
              Leave group
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

function ConversationList({ activeId, onNew, compact }: { activeId?: string; onNew: () => void; compact?: boolean }) {
  const me = useMe();
  const typing = useTyping((s) => s.typing);
  const { data, isPending, isError, error, refetch } = useConversations();
  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-line px-4">
        <h1 className="text-lg font-semibold">Messages</h1>
        <IconButton label="New message" icon={IconCompose} onClick={onNew} />
      </header>
      <div className="scroll-y min-h-0 flex-1 py-1">
        {isPending ? (
          Array.from({ length: 5 }, (_, i) => <RowSkeleton key={i} />)
        ) : isError ? (
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        ) : !data?.length ? (
          compact ? (
            <p className="px-4 py-3 text-sm text-fg-3">No conversations yet.</p>
          ) : (
            <EmptyState icon={IconMessages} title="No conversations yet" description="Message someone, or start a group." action={<Button variant="primary" onClick={onNew}>New message</Button>} />
          )
        ) : (
          data.map((c) => {
            const isTyping = Object.keys(typing[`d:${c.id}`] ?? {}).length > 0;
            const unread = c.unreadCount > 0 && c.id !== activeId;
            const last = c.lastMessage;
            const preview = last ? (last.kind === "system" ? `${last.author.id === me.id ? "You" : last.author.displayName} ${last.content}` : `${last.author.id === me.id ? "You: " : c.kind === "group" ? `${last.author.displayName}: ` : ""}${last.content || (last.ciphertext && last.e2ee !== "ok" ? encryptedPreview(last) : "Sent an image")}`) : "No messages yet";
            return (
              <ConversationContextMenu key={c.id} c={c} meId={me.id}>
                <Link
                  to={`/messages/${c.id}`}
                  aria-current={c.id === activeId ? "page" : undefined}
                  className={cn("mx-1.5 flex items-center gap-3 rounded-lg px-2.5 py-2.5 transition-colors", c.id === activeId ? "bg-surface-2" : "hover:bg-surface")}
                >
                  <ConversationAvatar c={c} meId={me.id} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={cn("truncate text-base", unread ? "font-semibold text-fg" : "font-medium text-fg")}>{conversationTitle(c, me.id)}</span>
                      {last && <span className="shrink-0 text-xs text-fg-3 tabular">{timeAgo(last.createdAt)}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn("flex-1 truncate text-sm", isTyping ? "text-accent" : unread ? "text-fg-2" : "text-fg-3")}>{isTyping ? "typing…" : preview}</span>
                      {unread && <Badge count={c.unreadCount} className="bg-accent" />}
                    </div>
                  </div>
                </Link>
              </ConversationContextMenu>
            );
          })
        )}
      </div>
    </div>
  );
}

function ConversationView({ id, mobile }: { id: string; mobile: boolean }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setFocus = useChatFocus((s) => s.setConversation);
  const typing = useTyping((s) => s.typing[`d:${id}`]);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [adding, setAdding] = useState(false);
  const key = useMemo(() => ["dm", id], [id]);

  const list = useConversations();
  const conv = useQuery({
    queryKey: ["conversation", id],
    queryFn: () => api.get<Conversation>(`/api/conversations/${id}`).then(decryptConversation),
    initialData: () => list.data?.find((c) => c.id === id),
  });
  const c = conv.data;
  useDocumentTitle(c ? conversationTitle(c, me.id) : "Messages");

  // End-to-end encryption: this device's key state, and whether we still trust everyone's keys.
  const e2ee = useE2ee();
  const encrypted = !!c?.encryption.enabled;
  const trust = useMemberTrust(c, me.id);
  const changedKeys = trust.rows.filter((r) => r.trust === "changed");
  const [verifying, setVerifying] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  // On by default: once this device is unlocked and everyone has keys, the next message turns encryption on.
  const autoEncrypt =
    useQuery({
      queryKey: ["e2ee-will-encrypt", id, c?.members.map((m) => m.id).join(","), e2ee.status],
      queryFn: () => willEncrypt(c!, true),
      enabled: !!c && !encrypted && e2ee.status === "ready",
      staleTime: 60_000,
    }).data === true;
  const secure = encrypted || autoEncrypt;
  const setConversation = useCallback((x: Conversation) => qc.setQueryData(["conversation", x.id], x), [qc]);

  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) =>
      api
        .get<MessagePage<DirectMessage>>(`/api/conversations/${id}/messages${qs({ before: pageParam as string | undefined, limit: 50 })}`)
        .then(async (p) => ({ ...p, items: await decryptDms(p.items) })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (l) => (l.hasMoreBefore ? l.items.find((m) => !m.id.startsWith("pending-"))?.id : undefined),
    ...chatQueryOptions,
  });
  const messages = useMemo(() => (query.data ? [...query.data.pages].reverse().flatMap((p) => p.items) : []), [query.data]);
  const lastId = (messages as ChatMessage[]).findLast((m) => !m.id.startsWith("pending-"))?.id;

  useEffect(() => {
    setFocus(id);
    setReplyTo(null);
    return () => setFocus(null);
  }, [id, setFocus]);

  // Read state.
  const readTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (!lastId) return;
    clearTimeout(readTimer.current);
    readTimer.current = setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      void api.post(`/api/conversations/${id}/read`, { messageId: lastId }).catch(() => {});
      qc.setQueryData<Conversation[]>(["conversations"], (old) => old?.map((x) => (x.id === id ? { ...x, unreadCount: 0 } : x)));
    }, 300);
    return () => clearTimeout(readTimer.current);
  }, [lastId, id, qc]);

  const send = useCallback(
    async ({ content, mediaIds, gif }: ComposerSend) => {
      const nonce = uuidv7();
      const reply = replyTo;
      setReplyTo(null);
      const optimistic = {
        id: `pending-${nonce}`,
        conversationId: id,
        author: { id: me.id, username: me.username, displayName: me.displayName, avatarUrl: me.avatarUrl },
        kind: "text" as const,
        content,
        createdAt: new Date().toISOString(),
        editedAt: null,
        replyTo: reply ? { id: reply.id, author: reply.author, content: reply.content, hasMedia: reply.media.length > 0 } : null,
        media: [],
        reactions: [],
        embeds: gif ? [gifEmbed(gif)] : [],
        forwardedFrom: null,
        stickers: [],
        pinnedAt: null,
        suppressEmbeds: false,
        nonce,
        pending: true,
      };
      upsertMessage(key, optimistic);
      try {
        // Images picked while encryption was (going to be) on were encrypted on upload; their keys go in the message.
        const attachments = mediaIds.length ? takeDescriptors(mediaIds) : null;
        let conv = c;
        // Plain images picked before that was known go as they are; the next message turns it on.
        if (conv && !conv.encryption.enabled && (attachments || (autoEncrypt && !mediaIds.length))) {
          try {
            conv = await ensureEncrypted(conv);
            if (conv !== c) setConversation(conv);
          } catch (e) {
            // Couldn't turn it on (someone's key changed, say): plain text stays plain, encrypted images can't go.
            if (attachments) throw e;
          }
        }
        if (conv?.encryption.enabled) {
          if (mediaIds.length && !attachments) throw new Error("This conversation is now end-to-end encrypted. Attach the images again so they're encrypted too.");
          const body = { content, ...(gif ? { gif } : {}), ...(attachments ? { attachments } : {}) };
          // Encrypt here; the server only ever sees ciphertext. Retries once if the key changed under us.
          const msg = await withFreshKey(
            conv,
            async (current) => {
              const { payload, conversation } = await encryptForConversation(current, body);
              if (conversation !== current) setConversation(conversation);
              return api.post<DirectMessage>(`/api/conversations/${id}/messages`, { content: "", encrypted: payload, mediaIds, replyToId: reply?.id, nonce });
            },
            setConversation,
          );
          rememberPlaintext(msg.id, content);
          const media = (attachments ?? []).map((a) => encryptedMedia(a, msg.media.find((x) => x.id === a.mediaId)?.url ?? `/media/${a.mediaId}`));
          upsertMessage(key, { ...msg, content, embeds: gif ? [gifEmbed(gif)] : [], media, e2ee: "ok" as const });
        } else {
          if (attachments) throw new Error("This conversation isn't end-to-end encrypted. Attach the images again.");
          const msg = await api.post<DirectMessage>(`/api/conversations/${id}/messages`, { content, mediaIds, replyToId: reply?.id, nonce, gif });
          upsertMessage(key, msg);
        }
      } catch (e) {
        patchMessage<typeof optimistic & { failed?: boolean }>(key, optimistic.id, (m) => ({ ...m, pending: false, failed: true }));
        if (e instanceof KeyChangedError) {
          trust.refresh();
          setVerifying(true);
        }
        toast.error(errorMessage(e, "Message not sent"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, key, me, replyTo, c, setConversation, autoEncrypt],
  );

  const actions: MessageActions = {
    canReply: true,
    canReact: false,
    canPin: false,
    canModerate: false,
    onReply: setReplyTo,
    messageLink: (m) => `${window.location.origin}/messages/${id}?m=${m.id}`,
    canAttach: !c?.encryption?.enabled,
    onEdit: async (m, content, mediaIds) => {
      try {
        if (c?.encryption.enabled) {
          if (mediaIds?.length) throw new Error("Images can't be added to an encrypted message after it's sent");
          // Keep a GIF and images the message already had: they live inside the ciphertext, so re-encrypt them with the new text.
          const g = m.embeds?.find((e) => e.type === "gif");
          const gif = g?.url && g.image ? { id: "", url: g.url, previewUrl: g.thumbnail?.url ?? g.url, width: g.image.width ?? 1, height: g.image.height ?? 1, title: g.title ?? "" } : undefined;
          const attachments = m.media.flatMap((x) => (x.e2ee ? [x.e2ee] : []));
          const updated = await withFreshKey(
            c,
            async (current) => {
              const { payload, conversation } = await encryptForConversation(current, { content, ...(gif ? { gif } : {}), ...(attachments.length ? { attachments } : {}) });
              if (conversation !== current) setConversation(conversation);
              return api.patch<DirectMessage>(`/api/direct-messages/${m.id}`, { encrypted: payload });
            },
            setConversation,
          );
          rememberPlaintext(m.id, content);
          patchMessage<DirectMessage>(key, m.id, () => ({ ...updated, content, embeds: m.embeds ?? [], media: m.media, e2ee: "ok" }));
          return;
        }
        const updated = await api.patch<DirectMessage>(`/api/direct-messages/${m.id}`, { content, ...(mediaIds?.length ? { mediaIds } : {}) });
        patchMessage<DirectMessage>(key, m.id, () => updated);
      } catch (e) {
        toast.error(errorMessage(e));
        throw e;
      }
    },
    onDelete: async (m) => {
      if (m.failed) return removeMessage(key, m.id);
      deleteMessageWithUndo(key, m.id, () => api.del(`/api/direct-messages/${m.id}`));
    },
  };

  const mentionSource = useCallback(
    async (q: string) => (c?.members ?? []).filter((u) => u.id !== me.id && (u.username.toLowerCase().startsWith(q.toLowerCase()) || u.displayName.toLowerCase().startsWith(q.toLowerCase()))).slice(0, 8),
    [c, me.id],
  );

  const leave = async () => {
    const ok = await confirm({ title: "Leave this group?", description: "You won't receive new messages unless someone adds you back.", confirmLabel: "Leave", danger: true });
    if (!ok) return;
    try {
      await api.post(`/api/conversations/${id}/leave`);
      qc.setQueryData<Conversation[]>(["conversations"], (old) => old?.filter((x) => x.id !== id));
      navigate("/messages");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const enableEncryption = async () => {
    if (!c) return;
    if (e2ee.status === "off") return setSettingUp(true);
    if (e2ee.status === "locked") return setUnlocking(true);
    if (e2ee.status !== "ready") return toast.error("Encrypted messages aren't available in this browser");
    const ok = await confirm({
      title: "Turn on end-to-end encryption?",
      description:
        "New messages and images here will only be readable by the people in this conversation, on their devices. This can't be turned off, and encrypted messages can't be searched. Everyone here needs to have signed in since encryption became the default.",
      confirmLabel: "Turn on",
    });
    if (!ok) return;
    try {
      const { distributeNewKey } = await import("../../lib/e2ee/session");
      setConversation(await distributeNewKey(c));
      toast.success("End-to-end encryption is on");
    } catch (e) {
      if (e instanceof KeyChangedError) {
        trust.refresh();
        setVerifying(true);
      }
      toast.error(errorMessage(e));
    }
  };

  if (conv.isError) {
    return conv.error instanceof ApiError && conv.error.status === 404 ? (
      <EmptyState title="Conversation not found" action={<Button onClick={() => navigate("/messages")}>Back to messages</Button>} className="h-full" />
    ) : (
      <ErrorState message={errorMessage(conv.error)} onRetry={() => void conv.refetch()} />
    );
  }

  // Read receipts: under your latest message, show who has seen it.
  const lastOwn = [...messages].reverse().find((m) => m.author.id === me.id && !("pending" in m && m.pending));
  const seenBy = lastOwn && c ? c.readStates.filter((r) => r.lastReadMessageId && r.lastReadMessageId >= lastOwn.id) : [];
  const typingNames = Object.values(typing ?? {}).map((t) => t.name);
  const others = c?.members.filter((m) => m.id !== me.id) ?? [];

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line px-3">
        {mobile && <IconButton label="Back" icon={IconBack} tooltip={false} onClick={() => navigate("/messages")} />}
        {c ? (
          <>
            {c.kind === "direct" && others[0] ? (
              <Link to={`/u/${others[0].username}`} className="flex min-w-0 items-center gap-3">
                <ConversationAvatar c={c} meId={me.id} size="md" />
                <div className="min-w-0">
                  <div className="truncate text-base font-semibold">{conversationTitle(c, me.id)}</div>
                  <div className="truncate text-xs text-fg-3">@{others[0].username}</div>
                </div>
              </Link>
            ) : (
              <div className="flex min-w-0 items-center gap-3">
                <ConversationAvatar c={c} meId={me.id} size="md" />
                <div className="min-w-0">
                  <div className="truncate text-base font-semibold">{conversationTitle(c, me.id)}</div>
                  <div className="truncate text-xs text-fg-3">{c.members.length} people</div>
                </div>
              </div>
            )}
            <div className="ml-auto flex items-center gap-1">
              {encrypted ? (
                <IconButton
                  label={changedKeys.length ? "Someone's security key changed" : "End-to-end encrypted. Verify security"}
                  icon={IconLock}
                  className={changedKeys.length ? "text-danger" : "text-accent"}
                  onClick={() => (e2ee.status === "ready" ? setVerifying(true) : setUnlocking(true))}
                />
              ) : (
                <IconButton
                  label={autoEncrypt ? "End-to-end encryption turns on with your next message" : "Not end-to-end encrypted yet. Turn it on"}
                  icon={IconLock}
                  className={autoEncrypt ? "text-accent" : undefined}
                  onClick={() => void enableEncryption()}
                />
              )}
              <CallButton conversation={c} title={conversationTitle(c, me.id)} />
            </div>
            {c.kind === "group" && (
              <Menu>
                <MenuTrigger asChild>
                  <IconButton label="Group options" icon={IconMore} tooltip={false} />
                </MenuTrigger>
                <MenuContent>
                  <MenuItem icon={IconEdit} onSelect={() => setRenaming(true)}>
                    Rename group
                  </MenuItem>
                  <MenuItem icon={IconUserPlus} onSelect={() => setAdding(true)}>
                    Add people
                  </MenuItem>
                  <MenuSeparator />
                  <MenuItem icon={IconLogout} danger onSelect={() => void leave()}>
                    Leave group
                  </MenuItem>
                </MenuContent>
              </Menu>
            )}
          </>
        ) : (
          <Spinner className="size-4 text-fg-3" />
        )}
      </header>
      {c && <CallPanel conversation={c} title={conversationTitle(c, me.id)} />}
      <MessageList
        messages={messages as ChatMessage[]}
        loading={query.isPending}
        hasMore={!!query.hasNextPage}
        loadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        meId={me.id}
        meUsername={me.username}
        actions={actions}
        intro={
          c && (
            <div className="flex flex-col items-center px-4 pb-4 pt-10 text-center">
              <ConversationAvatar c={c} meId={me.id} />
              <h2 className="mt-3 text-lg font-semibold">{conversationTitle(c, me.id)}</h2>
              <p className="text-sm text-fg-3">{c.kind === "direct" ? "This is the beginning of your conversation." : "This is the beginning of the group."}</p>
            </div>
          )
        }
        footer={
          seenBy.length > 0 && (
            <div className="flex items-center justify-end gap-1 px-4 pt-1 text-xs text-fg-3">
              <IconCheckRead className="icon size-3.5 text-accent" aria-hidden />
              {c?.kind === "direct" ? "Seen" : `Seen by ${seenBy.map((r) => c?.members.find((m) => m.id === r.userId)?.displayName).filter(Boolean).join(", ")}`}
            </div>
          )
        }
      />
      {encrypted && e2ee.status !== "ready" && (
        <div className="mx-3 mb-2 flex items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg-2">
          <IconLock className="icon size-4 shrink-0" aria-hidden />
          <span className="flex-1">
            {e2ee.status === "locked"
              ? "This conversation is end-to-end encrypted. Unlock encrypted messages on this device to read and send."
              : e2ee.status === "off"
                ? "This conversation is end-to-end encrypted. Set up encrypted messages to take part."
                : "This conversation is end-to-end encrypted, which this browser doesn't support."}
          </span>
          {e2ee.status === "locked" && (
            <Button size="sm" variant="primary" onClick={() => setUnlocking(true)}>
              Unlock
            </Button>
          )}
          {e2ee.status === "off" && (
            <Button size="sm" variant="primary" onClick={() => setSettingUp(true)}>
              Set up
            </Button>
          )}
        </div>
      )}
      {!encrypted && e2ee.status === "off" && (
        <div className="mx-3 mb-2 flex items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-fg-2">
          <IconLock className="icon size-4 shrink-0" aria-hidden />
          <span className="flex-1">Messages are end-to-end encrypted by default. Confirm your password to turn it on for your account.</span>
          <Button size="sm" variant="primary" onClick={() => setSettingUp(true)}>
            Turn on
          </Button>
        </div>
      )}
      {secure && e2ee.status === "ready" && <RecoveryCodeNudge />}
      {encrypted && e2ee.status === "ready" && changedKeys.length > 0 && (
        <div className="mx-3 mb-2 flex items-center gap-3 rounded-lg border border-danger/40 bg-danger-soft px-3 py-2 text-sm text-fg">
          <IconLock className="icon size-4 shrink-0 text-danger" aria-hidden />
          <span className="flex-1">
            {changedKeys.map((r) => r.user.displayName).join(", ")}
            {changedKeys.length === 1 ? "'s" : "'"} security key changed. That happens when someone resets their encryption, or if someone is trying to listen in. Check before sending.
          </span>
          <Button size="sm" onClick={() => setVerifying(true)}>
            Review
          </Button>
        </div>
      )}
      <ChatComposer
        draftKey={`d:${id}`}
        placeholder={c ? `Message ${conversationTitle(c, me.id)}` : "Message"}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onSend={(i) => void send(i)}
        disabledReason={encrypted && e2ee.status !== "ready" ? "Unlock encrypted messages to send here" : null}
        allowAttachments
        uploader={secure ? uploadEncryptedImage : undefined}
        allowGifs
        mentionSource={c?.kind === "group" ? mentionSource : undefined}
        onTyping={() => getSocket()?.emit("dm:typing", { conversationId: id })}
        typingNames={typingNames}
      />
      {c && <RenameDialog open={renaming} onOpenChange={setRenaming} conversation={c} />}
      {c && <AddPeopleDialog open={adding} onOpenChange={setAdding} conversation={c} />}
      <UnlockDialog open={unlocking} onOpenChange={setUnlocking} />
      <SetUpDialog open={settingUp} onOpenChange={setSettingUp} />
      <VerifyDialog open={verifying} onOpenChange={setVerifying} rows={trust.rows} onChanged={trust.refresh} />
    </div>
  );
}

function RenameDialog({ open, onOpenChange, conversation }: { open: boolean; onOpenChange: (o: boolean) => void; conversation: Conversation }) {
  const [name, setName] = useState(conversation.name ?? "");
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (open) setName(conversation.name ?? "");
  }, [open, conversation.name]);
  const save = async () => {
    setPending(true);
    try {
      await api.patch(`/api/conversations/${conversation.id}`, { name: name.trim() || null });
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Rename group"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={pending} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={50} placeholder="Group name" aria-label="Group name" onKeyDown={(e) => e.key === "Enter" && void save()} />
    </Dialog>
  );
}

function AddPeopleDialog({ open, onOpenChange, conversation }: { open: boolean; onOpenChange: (o: boolean) => void; conversation: Conversation }) {
  const [selected, setSelected] = useState<UserSummary[]>([]);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (!open) setSelected([]);
  }, [open]);
  const add = async () => {
    setPending(true);
    try {
      await api.patch(`/api/conversations/${conversation.id}`, { addUserIds: selected.map((u) => u.id) });
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add people"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!selected.length} loading={pending} onClick={() => void add()}>
            Add
          </Button>
        </>
      }
    >
      <PeoplePicker selected={selected} onChange={setSelected} exclude={conversation.members.map((m) => m.id)} />
    </Dialog>
  );
}

export function MessagesPage() {
  const { id } = useParams();
  const desktop = useIsDesktop();
  const [newOpen, setNewOpen] = useState(false);
  return (
    <div className="flex h-full">
      {(desktop || !id) && (
        <div className={cn("h-full shrink-0 border-line bg-bg-subtle", desktop ? "w-80 border-r xl:w-96" : "w-full")}>
          <ConversationList activeId={id} onNew={() => setNewOpen(true)} compact={desktop} />
        </div>
      )}
      {id ? (
        <ConversationView key={id} id={id} mobile={!desktop} />
      ) : (
        desktop && (
          <EmptyState
            icon={IconMessages}
            title="Your messages"
            description="Private conversations with people and small groups."
            action={<Button variant="primary" onClick={() => setNewOpen(true)}>New message</Button>}
            className="flex-1"
          />
        )
      )}
      <NewConversationDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}
